
import { TrendingUp, Clock, DollarSign, Shield } from "lucide-react";

interface MetricProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  description: string;
}

const MetricCard = ({ icon, value, label, description }: MetricProps) => (
  <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 flex flex-col items-center text-center h-full">
    <div className="mb-5 w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
      {icon}
    </div>
    <h3 className="text-4xl font-bold text-edgefi-purple mb-2">{value}</h3>
    <p className="text-xl font-semibold text-gray-900 mb-3">{label}</p>
    <p className="text-gray-600">{description}</p>
  </div>
);

interface ValueMetricsSectionProps {
  title: string;
  subtitle: string;
  description: string;
  metrics: Omit<MetricProps, 'icon'>[];
}

export const ValueMetricsSection = ({ title, subtitle, description, metrics }: ValueMetricsSectionProps) => {
  // Map of icons to use - reordering to match metric types better
  const getIconForMetric = (index: number, label: string) => {
    // Use specific icons based on the metric label content
    if (label.includes("Cost") || label.includes("cost")) {
      return <DollarSign className="h-8 w-8 text-edgefi-purple" />;
    } else if (label.includes("Monitoring") || label.includes("24/7")) {
      return <Clock className="h-8 w-8 text-edgefi-purple" />;
    } else if (label.includes("Alert") || label.includes("Response")) {
      return <TrendingUp className="h-8 w-8 text-edgefi-purple" />;
    } else if (label.includes("Threat") || label.includes("Reduction")) {
      return <Shield className="h-8 w-8 text-edgefi-purple" />;
    }
    
    // Fallback to the original order if no match
    const icons = [
      <TrendingUp className="h-8 w-8 text-edgefi-purple" />,
      <Shield className="h-8 w-8 text-edgefi-purple" />,
      <Clock className="h-8 w-8 text-edgefi-purple" />,
      <DollarSign className="h-8 w-8 text-edgefi-purple" />
    ];
    return icons[index % icons.length];
  };

  return (
    <section className="py-16 bg-gradient-to-b from-purple-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">
            {subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">{title}</h2>
          <p className="text-xl text-gray-600">{description}</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {metrics.map((metric, index) => (
            <MetricCard
              key={index}
              icon={getIconForMetric(index, metric.label)}
              value={metric.value}
              label={metric.label}
              description={metric.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
